import {
  ClockCircleOutlined,
  CloseOutlined,
  EditOutlined,
  LoadingOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MenuItem } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { DataContext } from "../../lib/contexts";
import { categoryToIcon } from "../../lib/definitions";
import { callEndpoint, doFormalize, getDishImages } from "../../lib/functions";
import { EcoLabel } from "../elements/ecoLabel";
import { MImg } from "../elements/mimg";
import { MInput } from "../elements/mInputs";
import { MSelect } from "../elements/mselect";
import { MModal } from "../elements/mmodal";
import { AddItemContent } from "../shopping/addItemContent";
import { CreateItemContent } from "../shopping/createItemContent";
import { FcOpenedFolder } from "react-icons/fc";
import { CircleButton } from "../elements/circleButton";
import { MButton } from "../elements/mbutton";
import { FaChevronLeft } from "react-icons/fa";
import {
  CreateDishEndpoint,
  Dish,
  DishCategory,
  ItemCategory,
  UpdateDishEndpoint,
} from "@ckal-software/ckal-lib/dist/apps/madplanen";
import { categoryToColor, ItemCategoryWithBought } from "../../lib/lib";
import { useNotifcation } from "../../lib/hooks/useNotification";
import { useScrollToTop } from "../../lib/hooks/useScrollToTop";
import css from "./upsertDish.module.css";

const NonFoodCategories: ItemCategoryWithBought[] = [
  ItemCategory.Beauty,
  ItemCategory.Electronics,
  ItemCategory.Garden,
  ItemCategory.Home,
  ItemCategory.Household,
  ItemCategory.Medicine,
  ItemCategory.Other,
  ItemCategory.Textile,
  "Købte",
];

export function UpsertDish() {
  const [dishName, setDishName] = useState("");
  const [description, setDescription] = useState("");
  const [dishIngredients, setDishIngredients] = useState<Dish["ingredients"]>(
    [],
  );
  const [hidden, setHidden] = useState(false);
  const [time, setTime] = useState(30);
  const [type, setType] = useState<DishCategory>(DishCategory.Dinner);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState("");
  const [isFetchingImages, setIsFetchingImages] = useState(false);
  const [hasSearchedImages, setHasSearchedImages] = useState(false);
  const [imageSearchRequest, setImageSearchRequest] = useState(0);
  const [itemSearchValue, setItemSearchValue] = useState("");
  const searchResultsRef = useScrollToTop(itemSearchValue);
  const [isUpsertingDish, setIsUpsertingDish] = useState(false);
  const [showCreateItemModal, setShowCreateItemModal] = useState(false);
  const [customImgUrl, setCustomImgUrl] = useState("");
  const [customImgUrlSelected, setCustomImgUrlSelected] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);

  const { items, itemsMap, refreshDishes, refreshFolders, dishes, folders } =
    useContext(DataContext);

  const navigate = useNavigate();
  const notify = useNotifcation();
  const { id } = useParams<{ id: string }>();

  const itemInputRef = useRef<HTMLInputElement>(null);
  const lastImageSearchRequest = useRef(imageSearchRequest);
  const timeOptions = [
    ...new Set([5, 10, 15, 20, 30, 45, 60, 90, 120, time]),
  ].sort((left, right) => left - right);
  const selectableFolders = folders.filter(
    (folder) => folder.folderName !== "Alle",
  );

  useEffect(() => {
    document.getElementById("upsert-dish-content")?.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (id) {
      const dish = dishes.find((d) => d._id === id);
      if (!dish) return;

      setDishName(dish.name);
      setDescription(dish.description);
      setDishIngredients(dish.ingredients);
      setType(dish.type);
      setTime(dish.time);
      setImageUrl(dish.imageUrl);
      setHidden(dish.hidden);
      setSelectedFolders(
        folders
          .filter((folder) => folder.dishIds.includes(dish._id))
          .map((folder) => folder._id),
      );
    }
  }, [dishes, id, folders]);

  useEffect(() => {
    if (!imageUrl) return;

    if (imageUrls.includes(imageUrl)) {
      setCustomImgUrlSelected(false);
    } else {
      setCustomImgUrl(imageUrl);
      setCustomImgUrlSelected(true);
    }
  }, [imageUrl, imageUrls]);

  useEffect(() => {
    const searchName = dishName.trim();
    const forceRefresh = lastImageSearchRequest.current !== imageSearchRequest;
    lastImageSearchRequest.current = imageSearchRequest;

    if (!searchName) {
      setImageUrls([]);
      setIsFetchingImages(false);
      setHasSearchedImages(false);
      return;
    }

    let cancelled = false;
    setImageUrls([]);
    setIsFetchingImages(true);
    setHasSearchedImages(false);

    const timeout = window.setTimeout(async () => {
      const urls = await getDishImages(searchName, forceRefresh);
      if (cancelled) return;

      setImageUrls(urls);
      setIsFetchingImages(false);
      setHasSearchedImages(true);
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [dishName, imageSearchRequest]);

  const ingredients = useMemo(
    () =>
      dishIngredients.map((ing) => ({
        ...itemsMap[ing.id],
        amount: ing.amount,
      })),
    [itemsMap, dishIngredients],
  );

  const sortedItemOptions = useMemo(() => {
    const normalizedQuery = doFormalize(itemSearchValue);
    const filteredItems = !itemSearchValue
      ? []
      : items
          .filter((item) => !NonFoodCategories.includes(item.category))
          .filter((item) =>
            doFormalize(
              (item.isOrganic ? "økologisk " : "") + item.name,
            ).includes(normalizedQuery),
          )
          .filter(
            (item) =>
              !ingredients.find((ingredient) => ingredient.name === item.name),
          );

    return filteredItems.sort((leftItem, rightItem) => {
      const leftName = doFormalize(leftItem.name);
      const rightName = doFormalize(rightItem.name);
      const leftRank =
        leftName === normalizedQuery
          ? 0
          : leftName.startsWith(normalizedQuery)
            ? 1
            : 2;
      const rightRank =
        rightName === normalizedQuery
          ? 0
          : rightName.startsWith(normalizedQuery)
            ? 1
            : 2;

      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      if (leftItem.popularity === rightItem.popularity) {
        return leftItem.name.localeCompare(rightItem.name);
      }

      return (rightItem.popularity || 0) - (leftItem.popularity || 0);
    });
  }, [itemSearchValue, items, ingredients]);

  const itemIsOnList = useMemo(
    () =>
      !!ingredients.find(
        (i) => doFormalize(i.name) === doFormalize(itemSearchValue),
      ),
    [ingredients, itemSearchValue],
  );

  function confirmItemName() {
    if (!itemSearchValue) return;

    const existingItem = sortedItemOptions.find(
      (item) => doFormalize(item.name) === doFormalize(itemSearchValue),
    );
    if (existingItem) {
      setDishIngredients((ings) =>
        ings.concat({ id: existingItem._id, amount: 1 }),
      );
    } else if (
      ingredients.find(
        (i) => doFormalize(i.name) === doFormalize(itemSearchValue),
      )
    ) {
      notify(`${itemSearchValue} er allerede på din indkøbsliste`);
    } else {
      setShowCreateItemModal(true);
    }
  }

  function handleIngredientAmountClick(ingredientId: string, amount?: number) {
    setDishIngredients((ings) =>
      ings.map((i) =>
        i.id === ingredientId ? { ...i, amount: amount ?? i.amount + 1 } : i,
      ),
    );
  }

  async function handleSave() {
    if (id) {
      const [err] = await callEndpoint(
        UpdateDishEndpoint,
        {
          id,
          update: {
            name: dishName.trim(),
            description: description.trim(),
            time,
            type,
            ingredients: dishIngredients,
            imageUrl: customImgUrlSelected ? customImgUrl : imageUrl,
            hidden,
          },
          inFolders: selectedFolders,
        },
        { setWorking: setIsUpsertingDish },
      );

      if (err) {
        notify("Opdatering af ret: Der skete en fejl");
        return;
      }

      refreshDishes();
      refreshFolders();
      const redirect =
        new URLSearchParams(window.location.search).get("redirect") || "";
      if (redirect) {
        navigate(redirect);
      } else {
        navigate(`/catalogue?selected-dish=${id}`);
      }
    } else {
      setIsUpsertingDish(true);

      const [err] = await callEndpoint(
        CreateDishEndpoint,
        {
          dish: {
            name: dishName,
            description,
            time,
            type,
            ingredients: dishIngredients,
            imageUrl: customImgUrlSelected ? customImgUrl : imageUrl,
            hidden,
          },
          inFolders: selectedFolders,
        },
        { setWorking: setIsUpsertingDish },
      );

      if (err) {
        notify("Oprettelse af ret: Der skete en fejl");
        return;
      }

      refreshDishes();
      refreshFolders();
      navigate("/catalogue");
    }
  }

  return (
    <div className={css.wrapper}>
      {/* Top bar */}
      <div className={css.topBar}>
        <span className={css.backButton} onClick={() => navigate(-1)}>
          <FaChevronLeft />
        </span>
        <span className={css.topTitle}>{id ? "Opdater ret" : "Ny ret"}</span>
      </div>

      {/* Content */}
      <div className={css.content} id="upsert-dish-content">
        {/* General section */}
        <div className={css.section}>
          <div className={css.sectionHeader}>Generelt</div>
          <div className={css.sectionContent}>
            <MInput
              size="large"
              placeholder="Navn på retten..."
              value={dishName}
              onChange={(e) => setDishName(e.target.value)}
              className="new-item-input"
            />
            <textarea
              placeholder="Beskrivelse..."
              className={css.descriptionInput}
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <div className={css.selectionControls}>
              <div className={css.selectionField}>
                <MSelect<number>
                  id="dish-time"
                  inputProps={{ "aria-label": "Tilberedningstid" }}
                  size="large"
                  value={time}
                  onChange={(event) => setTime(Number(event.target.value))}
                >
                  {timeOptions.map((minutes) => (
                    <MenuItem key={minutes} value={minutes}>
                      <ClockCircleOutlined
                        style={{ marginRight: 8, color: "#9ca3af" }}
                      />
                      {minutes} min
                    </MenuItem>
                  ))}
                </MSelect>
              </div>
              <div className={css.selectionField}>
                <MSelect<DishCategory>
                  id="dish-type"
                  inputProps={{ "aria-label": "Type af ret" }}
                  size="large"
                  value={type}
                  onChange={(event) =>
                    setType(event.target.value as DishCategory)
                  }
                >
                  {Object.values(DishCategory).map((dishType) => (
                    <MenuItem key={dishType} value={dishType}>
                      {dishType}
                    </MenuItem>
                  ))}
                </MSelect>
              </div>
              {selectableFolders.length > 0 && (
                <div className={css.selectionField}>
                  <MSelect<string[]>
                    id="dish-folders"
                    inputProps={{ "aria-label": "Mapper" }}
                    size="large"
                    multiple
                    displayEmpty
                    value={selectedFolders}
                    onChange={(event) => {
                      const value = event.target.value;
                      setSelectedFolders(
                        typeof value === "string" ? value.split(",") : value,
                      );
                    }}
                    renderValue={(folderIds) => (
                      <span className={css.folderValue}>
                        <FcOpenedFolder
                          style={{ fontSize: 20, flexShrink: 0 }}
                        />
                        <span className={css.folderValueText}>
                          {folderIds.length === 0
                            ? "Ingen mappe"
                            : selectableFolders
                                .filter((folder) =>
                                  folderIds.includes(folder._id),
                                )
                                .map((folder) => folder.folderName)
                                .join(", ")}
                        </span>
                      </span>
                    )}
                  >
                    {selectableFolders.map((folder) => (
                      <MenuItem key={folder._id} value={folder._id}>
                        <FcOpenedFolder
                          style={{ fontSize: 20, marginRight: 8 }}
                        />
                        {folder.folderName}
                      </MenuItem>
                    ))}
                  </MSelect>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Ingredients section */}
        <div className={css.section}>
          <div className={css.sectionHeader}>Ingredienser</div>
          <div className={css.sectionContent}>
            <div className={css.ingredientSearch}>
              <MInput
                prefix={<SearchOutlined className={css.ingredientSearchIcon} />}
                size="large"
                placeholder="Søg varer..."
                innerref={itemInputRef}
                allowClear
                value={itemSearchValue}
                onChange={(e) => setItemSearchValue(e.target.value)}
                suffix={
                  <PlusOutlined
                    style={{
                      color: "#3b82f6",
                      fontSize: "20px",
                      opacity: itemSearchValue ? 1 : 0.3,
                      marginLeft: "8px",
                    }}
                    onClick={confirmItemName}
                  />
                }
                onPressEnter={confirmItemName}
                className="new-item-input"
              />
            </div>
            <div className={css.ingredientResults} ref={searchResultsRef}>
              <AddItemContent
                onNewItem={() => setShowCreateItemModal(true)}
                small
                itemsOnList={dishIngredients.map((ing) => ({
                  _id: ing.id,
                  amount: ing.amount,
                }))}
                sortedItemOptions={sortedItemOptions}
                currentItemName={itemSearchValue}
                itemIsOnList={itemIsOnList}
                isIngredients
                addItemToList={(item) => {
                  itemInputRef.current?.focus();
                  setDishIngredients((ings) =>
                    ings.concat({ id: item._id, amount: 1 }),
                  );
                  setItemSearchValue("");
                }}
              />
            </div>

            {/* Ingredient list */}
            <div className={css.ingredientList}>
              {dishIngredients.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "#9ca3af",
                    padding: "12px 0",
                  }}
                >
                  Ingen ingredienser tilføjet...
                </div>
              ) : (
                ingredients
                  .sort(
                    (a, b) =>
                      a.category.localeCompare(b.category) * 10 +
                      a.name.localeCompare(b.name),
                  )
                  .map((ingredient) => (
                    <div key={ingredient._id} className={css.ingredientRow}>
                      <span
                        className={css.ingredientIcon}
                        style={{
                          background: `color-mix(in srgb, ${categoryToColor[ingredient.category]} 15%, transparent)`,
                        }}
                      >
                        {categoryToIcon[ingredient.category]}
                      </span>
                      <span className={css.ingredientName}>
                        {ingredient.name}
                      </span>
                      {ingredient.isOrganic && <EcoLabel size={1} />}
                      <CircleButton
                        onClick={() =>
                          handleIngredientAmountClick(ingredient._id)
                        }
                        onLongPress={() =>
                          handleIngredientAmountClick(ingredient._id, 1)
                        }
                        text={ingredient.amount}
                        style={{
                          width: 28,
                          minWidth: 28,
                          height: 28,
                          borderRadius: 8,
                          boxSizing: "border-box",
                          fontSize: 13,
                          fontWeight: 600,
                          flexShrink: 0,
                          background:
                            ingredient.amount !== 1 ? "#eff6ff" : "#f3f4f6",
                          color:
                            ingredient.amount !== 1 ? "#2563eb" : "#6b7280",
                          border: `1px solid ${ingredient.amount !== 1 ? "#bfdbfe" : "#e5e7eb"}`,
                        }}
                      />
                      <button
                        type="button"
                        className={css.ingredientDelete}
                        title="Fjern ingrediens"
                        aria-label={`Fjern ${ingredient.name}`}
                        onClick={() =>
                          setDishIngredients((currentIngredients) =>
                            currentIngredients.filter(
                              (currentIngredient) =>
                                currentIngredient.id !== ingredient._id,
                            ),
                          )
                        }
                      >
                        <CloseOutlined />
                      </button>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>

        {/* Image section */}
        <div className={css.section}>
          <div className={css.sectionHeader}>Billede</div>
          <div className={css.sectionContent}>
            {isFetchingImages ? (
              <div className={css.imageStatus}>
                <LoadingOutlined /> Søger efter billeder...
              </div>
            ) : imageUrls.length > 0 ? (
              <div className={css.imageGrid}>
                {imageUrls.map((url, index) => (
                  <div
                    key={url + index}
                    className={`${css.imageOption} ${!customImgUrlSelected && imageUrl === url ? css.imageOptionSelected : ""}`}
                    onClick={() => {
                      setImageUrl(url);
                      setCustomImgUrl("");
                      setCustomImgUrlSelected(false);
                    }}
                  >
                    <MImg url={url} extraClassName="mimg__select" />
                  </div>
                ))}
              </div>
            ) : (
              <div className={css.imageStatus}>
                <span>
                  {dishName.trim() && hasSearchedImages
                    ? "Ingen billedforslag fundet"
                    : "Angiv rettens navn for at få billedforslag"}
                </span>
                {dishName.trim() && hasSearchedImages && (
                  <MButton onClick={() => setImageSearchRequest((n) => n + 1)}>
                    Søg igen
                  </MButton>
                )}
              </div>
            )}

            <div className={css.imageDivider}>eller</div>
            <MInput
              size="large"
              placeholder="Egen billede-URL..."
              value={customImgUrl}
              allowClear
              className="new-item-input"
              onChange={(e) => {
                setCustomImgUrl(e.target.value);
                setCustomImgUrlSelected(!!e.target.value);
              }}
            />
            {customImgUrl && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 12,
                }}
                onClick={() => setCustomImgUrlSelected(true)}
              >
                <div
                  className={`${css.imageOption} ${customImgUrlSelected ? css.imageOptionSelected : ""}`}
                  style={{ width: "50%" }}
                >
                  <MImg url={customImgUrl} extraClassName="mimg__select" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className={css.actions}>
          <MButton
            type="primary"
            icon={id ? <EditOutlined /> : <PlusOutlined />}
            loading={isUpsertingDish}
            style={{ width: "100%" }}
            disabled={
              !dishName.trim() ||
              (!imageUrl && !customImgUrl) ||
              isUpsertingDish
            }
            onClick={handleSave}
          >
            {id ? "Opdater" : "Opret"}
          </MButton>
        </div>
      </div>

      {/* Create item modal */}
      <MModal
        visible={showCreateItemModal}
        onClose={() => {
          setShowCreateItemModal(false);
          itemInputRef.current?.focus();
        }}
        title="Opret ny vare"
        hideCloseIcon
        content={
          <CreateItemContent
            allowOther={false}
            onClose={() => {
              setShowCreateItemModal(false);
              itemInputRef.current?.focus();
            }}
            onFinish={(itemId) => {
              itemInputRef.current?.focus();
              setShowCreateItemModal(false);
              setDishIngredients((ings) =>
                ings.concat({ id: itemId, amount: 1 }),
              );
              setItemSearchValue("");
            }}
            initialItemName={itemSearchValue}
          />
        }
      />
    </div>
  );
}
